<template>
  <div>
    <div class="mb-2 d-flex align-items-center gap-2">
      <a href="#" class="d-flex align-items-center gap-2" @click.prevent="$router.go(-1)">
        <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
        {{ $t('Back to Selection') }}
      </a>
    </div>

    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="instructionsForm">
        <b-form ref="instructionsFormElement" class="" @submit.prevent="submit">
          <b-row>
            <b-col cols="12">
              <div class="instructions-list">
                <template v-if="loading">
                  <div v-for="i in 3" :key="i" class="instruction-item">
                    <b-skeleton width="40%" height="24px" class="mb-0" />
                    <b-skeleton width="20%" height="24px" class="mb-0" />
                    <b-skeleton width="20%" height="24px" class="mb-0" />
                  </div>
                </template>
                <template v-else-if="instructions && instructions.length > 0">
                  <div class="instruction-item-header">
                    <div class="instruction-name">
                      <label>{{ $t('Additional Service Name') }}</label>
                    </div>
                    <div class="instruction-price-type">
                      <label>{{ $t('Price Type') }}</label>
                    </div>
                    <div class="instruction-price-value">
                      <label>{{ $t('Cost Price Value') }}</label>
                    </div>
                    <div class="instruction-price-value">
                      <label>{{ $t('Sale Price Value') }}</label>
                    </div>
                  </div>
                  <div
                    v-for="instruction in instructions"
                    :key="instruction.id"
                    class="instruction-item"
                  >
                    <!-- Service Name (Readonly) -->
                    <div class="instruction-name">
                      <label>{{ instruction.description }}</label>
                    </div>

                    <!-- Price Type -->
                    <div class="instruction-price-type">
                      <label>{{ $t('Fixed') }}</label>
                    </div>
                    <!-- Cost Price Value-->
                    <div class="instruction-price-type">
                      <label>{{ instruction.price }}</label>
                    </div>

                    <!-- Price Value Input -->
                    <div class="instruction-price-value">
                      <validation-provider
                        #default="{ errors }"
                        :name="`${$t('Sale Price Value')} - ${instruction.description}`"
                        :vid="`salePrice_${instruction.id}`"
                        :rules="getValidationRules()"
                      >
                        <b-form-input
                          type="number"
                          v-model.number="instruction.salePrice"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          :state="
                            errors[0]
                              ? false
                              : submittedOnce && isEmpty(instruction.salePrice)
                              ? false
                              : null
                          "
                        />
                        <small class="text-danger">
                          {{
                            errors[0] ||
                            (submittedOnce && isEmpty(instruction.salePrice)
                              ? $t('This field is required')
                              : '')
                          }}
                        </small>
                      </validation-provider>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="empty-state text-center p-4">
                    <feather-icon icon="FileTextIcon" size="48" class="mb-2" />
                    <h4>{{ $t('No Instruction Details') }}</h4>
                    <p class="text-muted">
                      {{ $t('Instruction details are not available.') }}
                    </p>
                    <b-button variant="outline-primary" class="mt-2" @click="$router.go(-1)">
                      <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
                      {{ $t('Back to List') }}
                    </b-button>
                  </div>
                </template>
              </div>
            </b-col>

            <!-- Submit Button -->
            <b-col cols="12">
              <div
                class="d-flex gap-3 align-items-center justify-content-end mt-2"
                style="gap: 12px"
              >
                <b-button
                  variant="primary"
                  class="d-flex align-items-center"
                  type="submit"
                  :disabled="instructions.length === 0"
                >
                  {{ submitting ? $t('Please Wait') : $t('Save') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>

<script>
import { BCol, BForm, BRow, BButton, BOverlay, BFormInput, BSkeleton } from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';

export default {
  name: 'SalePricesInstructionsDetail',

  components: {
    BOverlay,
    BButton,
    BRow,
    BCol,
    BForm,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    BSkeleton
  },

  mixins: [helper],

  data() {
    return {
      instructions: [],
      submitting: false,
      loading: false,
      submittedOnce: false
    };
  },

  created() {
    const { providerId, customerId } = this.$route.params;
    if (!providerId || !customerId) {
      this.showErrorMessage(this.$t('Invalid route parameters'));
      this.$router.go(-1);
      return;
    }
    this.fetchInstructionDetails();
  },

  methods: {
    isEmpty(value) {
      return value === null || value === undefined || value === '';
    },
    async fetchInstructionDetails() {
      try {
        this.loading = true;
        const { providerId, customerId } = this.$route.params;
        const response = await useService.providerService.getSalePricesInstructionsDetail(
          providerId,
          customerId
        );

        if (response.data && Array.isArray(response.data)) {
          this.instructions = response.data;
        } else {
          this.instructions = [];
        }
      } catch (error) {
        this.showErrorMessage(
          error.response?.data?.message || this.$t('Error fetching instruction details')
        );
      } finally {
        this.loading = false;
      }
    },

    getValidationRules() {
      const rules = ['required', 'doubleOrInteger', 'positive'];
      return rules.join('|');
    },

    submit() {
      this.$refs.instructionsForm.validate().then(success => {
        if (success) {
          const hasNulls = this.instructions.some(item => this.isEmpty(item.salePrice));
          if (hasNulls) {
            this.submittedOnce = true;
            return;
          }
          const { providerId, customerId } = this.$route.params;
          const servicesData = this.instructions.map(item => ({
            provider_action_support_id: item.provider_action_support_id,
            salePrice: parseFloat(item.salePrice)
          }));

          this.submitting = true;

          useService.providerService
            .updateSalePricesInstructions(providerId, customerId, { prices: servicesData })
            .then(() => {
              this.showSuccessMessage(this.$t('Sale prices updated successfully'));
              this.$router.go(-1);
            })
            .catch(error => {
              this.showErrorMessage(
                error.response?.data?.message || this.$t('Failed to update sale prices')
              );
            })
            .finally(() => {
              this.submitting = false;
            });
        } else {
          this.submittedOnce = true;
        }
      });
    }
  }
};
</script>

<style scoped>
.instructions-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.instruction-item {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.instruction-item-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 1rem;
  border-radius: 4px;
  padding: 0 1rem;
}

.instruction-name {
  display: flex;
  align-items: center;
}

.instruction-name label {
  margin: 0;
  font-weight: 500;
}

.instruction-price-type,
.instruction-price-value {
  min-width: 200px;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
